/**
 * gfsData.ts
 *
 * Loads and samples the real NOAA GFS data prepared by
 * scripts/fetchGfsData.mjs + scripts/decode_gfs.py: a manifest describing
 * the available pressure levels, one binary file per level (U, V, DZDT,
 * geopotential height, temperature), and one surface MSLP file.
 *
 * Longitude convention note: GFS's native grid runs 0..360 degrees east,
 * while the rest of this app uses -180..180; conversion happens at sample
 * time here so callers can keep using the app's usual convention.
 *
 * The actual grid-sampling math (sampleBlock/sampleWindFromLevels/sampleMslpFromSurface) lives
 * in gfsSampling.ts, not here -- it's shared verbatim with the particle physics Web Worker
 * (see workers/particlePhysicsWorker.ts), which receives this store's ALREADY-fetched data via
 * transferred copies (see getTransferableLevel/getTransferableSurface below) rather than
 * fetching it a second time over the network.
 */

import { sampleMslpFromSurface, sampleWindFromLevels } from "./gfsSampling";
import type { LevelData, SurfaceData, VariableBlock, WindSample } from "./gfsSampling";

export interface GfsManifest {
  cycle: string;
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  levels: Array<{ hpa: number; file: string }>;
  surfaceFile: string;
}

export type { WindSample };

/** A standalone (independently-owned, safe-to-transfer) copy of one variable's packed data. */
export interface TransferableVariableBlock {
  scale: number;
  offset: number;
  buffer: ArrayBuffer;
}

export interface TransferableLevel {
  hpa: number;
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  u: TransferableVariableBlock;
  v: TransferableVariableBlock;
  wz: TransferableVariableBlock;
  gh: TransferableVariableBlock;
  t: TransferableVariableBlock;
}

export interface TransferableSurface {
  numLat: number;
  numLon: number;
  latStart: number;
  lonStart: number;
  latStepDeg: number;
  lonStepDeg: number;
  field: TransferableVariableBlock;
}

/** Copies a variable block's data into a fresh, standalone ArrayBuffer safe to `postMessage`
 *  with a transfer list (the original stays a view into GfsDataStore's own fetched buffer, so
 *  transferring it directly would detach/corrupt this store's own copy). */
function toTransferableBlock(block: VariableBlock): TransferableVariableBlock {
  const copy = block.data.slice(); // fresh Int16Array backed by its OWN new ArrayBuffer
  return { scale: block.scale, offset: block.offset, buffer: copy.buffer };
}

const DATA_BASE = "/data/gfs";

function readHeader(view: DataView) {
  const numLat = view.getInt32(0, true);
  const numLon = view.getInt32(4, true);
  const latStart = view.getFloat64(8, true);
  const lonStart = view.getFloat64(16, true);
  const latStepDeg = view.getFloat64(24, true);
  const lonStepDeg = view.getFloat64(32, true);
  return { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg };
}

function readVariableBlock(buffer: ArrayBuffer, byteOffset: number, count: number): { block: VariableBlock; nextOffset: number } {
  const view = new DataView(buffer, byteOffset, 8);
  const scale = view.getFloat32(0, true);
  const offset = view.getFloat32(4, true);
  const data = new Int16Array(buffer, byteOffset + 8, count);
  return { block: { scale, offset, data }, nextOffset: byteOffset + 8 + count * 2 };
}

function parseLevelFile(buffer: ArrayBuffer): LevelData {
  const headerView = new DataView(buffer, 0, 40);
  const { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg } = readHeader(headerView);
  const hpa = new DataView(buffer, 40, 4).getFloat32(0, true);
  const count = numLat * numLon;

  let offset = 44;
  const names = ["u", "v", "wz", "gh", "t"] as const;
  const blocks: Record<string, VariableBlock> = {};
  for (const name of names) {
    const { block, nextOffset } = readVariableBlock(buffer, offset, count);
    blocks[name] = block;
    offset = nextOffset;
  }

  return {
    hpa,
    numLat,
    numLon,
    latStart,
    lonStart,
    latStepDeg,
    lonStepDeg,
    u: blocks.u,
    v: blocks.v,
    wz: blocks.wz,
    gh: blocks.gh,
    t: blocks.t
  };
}

function parseSurfaceFile(buffer: ArrayBuffer): SurfaceData {
  const headerView = new DataView(buffer, 0, 40);
  const { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg } = readHeader(headerView);
  const count = numLat * numLon;
  const { block } = readVariableBlock(buffer, 40, count);
  return { numLat, numLon, latStart, lonStart, latStepDeg, lonStepDeg, field: block };
}

/** Bilinear-samples a packed variable block at (latDeg, lonDeg in -180..180) -- see sampleBlock
 *  in gfsSampling.ts (this is the single shared implementation; both the main thread and the
 *  particle physics worker use it). */
export class GfsDataStore {
  private manifest: GfsManifest | null = null;
  private surface: SurfaceData | null = null;
  private readonly levelCache = new Map<number, LevelData>();
  private readonly pendingLoads = new Map<number, Promise<LevelData>>();
  /** Cached surface-first sorted snapshot of levelCache, invalidated whenever a new level loads. */
  private sortedLevelsCache: LevelData[] | null = null;

  async loadManifestAndSurface(): Promise<GfsManifest> {
    const manifestResponse = await fetch(`${DATA_BASE}/manifest.json`);
    if (!manifestResponse.ok) throw new Error(`Failed to load GFS manifest: HTTP ${manifestResponse.status}`);
    const manifest = (await manifestResponse.json()) as GfsManifest;
    this.manifest = manifest;

    const surfaceResponse = await fetch(`${DATA_BASE}/${manifest.surfaceFile}`);
    if (!surfaceResponse.ok) throw new Error(`Failed to load GFS surface data: HTTP ${surfaceResponse.status}`);
    this.surface = parseSurfaceFile(await surfaceResponse.arrayBuffer());

    return manifest;
  }

  getManifest(): GfsManifest {
    if (!this.manifest) throw new Error("GFS manifest not loaded yet; call loadManifestAndSurface() first.");
    return this.manifest;
  }

  /** Ensures every level within [minHpa, maxHpa] (inclusive) is loaded, returning when all are ready. */
  async ensureLevelsLoaded(minHpa: number, maxHpa: number, onProgress?: (loaded: number, total: number) => void): Promise<void> {
    const manifest = this.getManifest();
    const needed = manifest.levels.filter((l) => l.hpa >= minHpa - 1e-6 && l.hpa <= maxHpa + 1e-6);
    let loaded = 0;
    await Promise.all(
      needed.map(async (levelInfo) => {
        await this.loadLevel(levelInfo.hpa, levelInfo.file);
        loaded++;
        onProgress?.(loaded, needed.length);
      })
    );
  }

  private loadLevel(hpa: number, file: string): Promise<LevelData> {
    const cached = this.levelCache.get(hpa);
    if (cached) return Promise.resolve(cached);

    const pending = this.pendingLoads.get(hpa);
    if (pending) return pending;

    const promise = fetch(`${DATA_BASE}/${file}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load GFS level ${hpa}: HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        const level = parseLevelFile(buffer);
        this.levelCache.set(hpa, level);
        this.sortedLevelsCache = null; // invalidate; rebuilt lazily on next sampleWind() call
        this.pendingLoads.delete(hpa);
        return level;
      });
    this.pendingLoads.set(hpa, promise);
    return promise;
  }

  isLevelLoaded(hpa: number): boolean {
    return this.levelCache.has(hpa);
  }

  /** Lazily rebuilds the surface-first sorted levels snapshot only when the cache has changed. */
  private getSortedLevels(): LevelData[] {
    if (!this.sortedLevelsCache) {
      this.sortedLevelsCache = [...this.levelCache.values()].sort((a, b) => b.hpa - a.hpa);
    }
    return this.sortedLevelsCache;
  }

  /** Samples real GFS wind/height/temperature, log-pressure interpolated between the two nearest LOADED levels. */
  sampleWind(latDeg: number, lonDeg: number, pressureHpa: number): WindSample {
    return sampleWindFromLevels(this.getSortedLevels(), latDeg, lonDeg, pressureHpa);
  }

  sampleMslpHpa(latDeg: number, lonDeg: number): number {
    return sampleMslpFromSurface(this.surface, latDeg, lonDeg);
  }

  /** hPa values of every level currently loaded, for diffing against what's already been
   *  forwarded to the particle physics worker (see main.ts). */
  getLoadedHpas(): number[] {
    return [...this.levelCache.keys()];
  }

  /** A standalone, transfer-safe copy of one loaded level's data, or null if not loaded. */
  getTransferableLevel(hpa: number): TransferableLevel | null {
    const level = this.levelCache.get(hpa);
    if (!level) return null;
    return {
      hpa: level.hpa,
      numLat: level.numLat,
      numLon: level.numLon,
      latStart: level.latStart,
      lonStart: level.lonStart,
      latStepDeg: level.latStepDeg,
      lonStepDeg: level.lonStepDeg,
      u: toTransferableBlock(level.u),
      v: toTransferableBlock(level.v),
      wz: toTransferableBlock(level.wz),
      gh: toTransferableBlock(level.gh),
      t: toTransferableBlock(level.t)
    };
  }

  /** A standalone, transfer-safe copy of the surface MSLP data, or null if not loaded. */
  getTransferableSurface(): TransferableSurface | null {
    if (!this.surface) return null;
    return {
      numLat: this.surface.numLat,
      numLon: this.surface.numLon,
      latStart: this.surface.latStart,
      lonStart: this.surface.lonStart,
      latStepDeg: this.surface.latStepDeg,
      lonStepDeg: this.surface.lonStepDeg,
      field: toTransferableBlock(this.surface.field)
    };
  }
}

